import { useState } from 'react';
import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import { DialogFooter } from '#/modules/commonComponents/dialog/DialogPrimitives';
import {
  dialogContentCompactGapClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import { toast } from '#/utils/toast';

/**
 * 退回 Dialog 開啟時掛載，reason 自然重置。
 * 表單在 body；取消／確認走 AppDialog `footer` 殼層（destructive 確認鈕）。
 */
const RejectReasonDialogSession = ({
  open,
  onOpenChange,
  title,
  description,
  confirmText,
  isPending,
  onConfirm,
  reasonLabel,
  emptyErrorMessage,
}) => {
  const [reason, setReason] = useState('');

  function handleCancel() {
    onOpenChange(false);
  }

  function handleConfirm() {
    if (isPending) {
      return;
    }
    const trimmed = reason.trim();
    if (!trimmed) {
      toast.error(emptyErrorMessage);
      return;
    }
    onConfirm?.(trimmed);
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      headerVariant="muted"
      contentClassName={dialogContentCompactGapClassName}
      closeOnOutsideClick={false}
      footer={(
        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleCancel}>
            取消
          </Button>
          <Button
            type="button"
            variant="destructive"
            className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
            disabled={!reason.trim() || isPending}
            onClick={handleConfirm}
          >
            {isPending ? `${confirmText}中…` : confirmText}
          </Button>
        </DialogFooter>
      )}
    >
      <FloatingTextarea
        id="document-reject-reason"
        label={reasonLabel}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        containerClassName="pt-4"
        rows={3}
      />
    </AppDialog>
  );
};

RejectReasonDialogSession.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  confirmText: PropTypes.string.isRequired,
  isPending: PropTypes.bool,
  onConfirm: PropTypes.func,
  reasonLabel: PropTypes.string.isRequired,
  emptyErrorMessage: PropTypes.string.isRequired,
};

/**
 * 單據退回：必填原因 Dialog（SideMsg／CommandBar 共用）。
 */
const DocumentRejectReasonDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmText = '退回',
  isPending = false,
  onConfirm,
  reasonLabel = '退回原因',
  emptyErrorMessage = '請填寫退回原因',
}) => {
  if (!open) {
    return (
      <AppDialog
        open={false}
        onOpenChange={onOpenChange}
        title={title}
        description={description}
        headerVariant="muted"
      />
    );
  }

  return (
    <RejectReasonDialogSession
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      confirmText={confirmText}
      isPending={isPending}
      onConfirm={onConfirm}
      reasonLabel={reasonLabel}
      emptyErrorMessage={emptyErrorMessage}
    />
  );
};

DocumentRejectReasonDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  confirmText: PropTypes.string,
  isPending: PropTypes.bool,
  onConfirm: PropTypes.func,
  reasonLabel: PropTypes.string,
  emptyErrorMessage: PropTypes.string,
};

export default DocumentRejectReasonDialog;
